import { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import styles from './programDetails.module.scss';
import Header from '../../components/header/Header';
import { gql, useMutation } from '@apollo/client';
import { useHistory, useParams } from 'react-router-dom';
import SelectedContent from './selected-content/SelectedContent';
import { CourseContentProvider, useCourseContent } from '../../hooks/useCourseContent';
import Spinner from '../../components/spinner/Spinner';
import { useQueryParams } from '../../hooks/useQueryParams';
import ChevronRight from '../../assets/icons/chewron_right.svg?react';
import RotateIcon from '../../assets/icons/rotate.svg?react';
import ListIcon from '../../assets/icons/list.svg?react';
import LessonHeader from './lesson-header/LessonHeader';
import {
  useGetProgramByIdLazyQuery,
  UnitContentFragment,
  Program,
  LastVisitedLessonContentFragment
} from 'src/api/graphql-codegen';
import { useMediaQuery } from 'react-responsive';
import SidebarUnit from './SidebarUnit';

type Props = {
  isParentApp?: boolean;
  isParentSpecialProgram?: boolean;
};

const updateLastUsedMutation = gql`
  mutation UpdateLastUsed($programId: ID!) {
    updateProgramLastUsed(programId: $programId) {
      lastUsed
      programId
      userId
    }
  }
`;

type UpdateLastUsedMutationResponse = {
  updateProgramLastUsed: {
    programId: string;
    userId: string;
    lastUsed: string;
  };
}

type Params = { id: string };

export type LastVisitedLesson = LastVisitedLessonContentFragment;

function Component({ isParentApp, isParentSpecialProgram }: Props) {
  const {id} = useParams<Params>();
  const params = useQueryParams();
  const history = useHistory();
  const fromBeginning = useMemo(() => Boolean(params.get('beginning')), [params]);
  const isPortrait = useMediaQuery({ query: '(orientation: portrait)' });
  const isMobile = useMediaQuery({ maxWidth: 915 });

  const [showList, setShowList] = useState(false);

  const [updateLastUsed] = useMutation<UpdateLastUsedMutationResponse>(updateLastUsedMutation, {
    update(cache, res) {
      const d = res.data?.updateProgramLastUsed;
      if (d) {
        cache.writeFragment({
          id: `Program:${d.programId}`,
          data: {
            lastUsed: d.lastUsed,
          },
          fragment: gql`
            fragment LastUsed on Program {
              lastUsed
            }
          `,
        });
      }
    }
  });

  const [getProgram, {data: programData, loading, error}] = useGetProgramByIdLazyQuery({variables: { input: { id } }, fetchPolicy: 'no-cache'});
  // const [getParentProgram, { data: parentProgramData, loading: parentLoading, error: parentError }] = useGetParentProgramByIdLazyQuery({variables: {id}, fetchPolicy: 'no-cache'});
  // const [getParentSpecialProgram, { data: parentSpecialProgramData, loading: parentSpecialLoading, error: parentSpecialError }] = useGetParentSpecialProgramByIdLazyQuery({variables: {id}, fetchPolicy: 'no-cache'});
  // const data = isParentSpecialProgram ? parentSpecialProgramData : (isParentApp ? parentProgramData : programData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // const programLessons: any[] = []; //isParentSpecialProgram ? parentSpecialProgramData?.parentSpecialProgramLessons : (isParentApp ? parentProgramData?.parentProgramLessons : programData?.getProgramLessons);
  const units: UnitContentFragment[] = useMemo(() => [...(programData?.getCourse.primary || []), ...(programData?.getCourse.additional || [])], [programData?.getCourse.additional, programData?.getCourse.primary]);
  useEffect(() => {
    if (id) {
      getProgram({
        variables: {
          input: { id },
        },
      })
      if (isParentSpecialProgram) {
        // getParentSpecialProgram({
        //   variables: {
        //     id,
        //   },
        // })
      } else {
        if (isParentApp) {
          // getParentProgram({
          //   variables: {
          //     id,
          //   },
          // })
        } else {
          updateLastUsed({
            variables: {
              programId: id,
            }
          });
        }
      }
    }
  }, [getProgram, id, isParentApp, isParentSpecialProgram, updateLastUsed]);

  const {
    content: selectedContent,
    program: selectedProgram,
    setProgram: setSelectedProgram,
    lesson,
    clear,
    selectionLevel,
    currentUnit,
  } = useCourseContent();

  useEffect(() => {
    if (fromBeginning) {
      history.replace(history.location.pathname);
    }
  }, [fromBeginning, history]);

  useEffect(() => {
    if (programData?.getCourse?.id && !selectedProgram) {
      setSelectedProgram(programData.getCourse as Program);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programData?.getCourse.id, selectedProgram, setSelectedProgram]);

  useEffect(() => {
    return () => clear();
  }, [clear]);

  useEffect(() => {
    document.body.style.overflow = showList ? "hidden" : "auto";
    document.getElementById("root")!.style.overflow = showList ? "hidden" : "auto";
  }, [showList])

  if (error) { //  || parentError || parentSpecialError
    return (
      <>
        <Header/>
        <div className={styles.page}>
          <div className={styles.content}>
            {(error)?.message}
          </div>
        </div>
      </>
    )
  }

  const handleBack = () => {
    if (isParentApp) {
      history.push('/parent/dashboard');
    } else {
      if (history.length > 0) {
        history.goBack();
      } else {
        history.push('/dashboard');
      }
    }
  };

  const toggleHandler = (close?: boolean) => {
    if (close) setShowList(false);
    else setShowList(!showList);
  }
  const listSection =
    <div className={styles.list}>
      <div className={styles.programNameSection}>
        <div onClick={handleBack} className={styles.backBtn}>
          <ChevronRight/>
        </div>
        <div>{programData?.getCourse.name}</div>
      </div>
      {units.map((unit, ind) => (
        <SidebarUnit unit={unit as any} lastVisitedLesson={undefined} isParentApp={isParentApp} key={ind} />
      ))}
    </div>;

  const normalTmpl = loading ? //  || parentLoading || parentSpecialLoading
    (<div className={styles.loading}><Spinner/></div>) :
    (<div className={styles.page}>
      {(!isParentApp || !isMobile) && listSection}
      <div className={styles.content}>
        {selectedProgram &&
          (
            <>
              {isMobile && !isPortrait && (
                <>
                  <div className={styles.programNameSection}>
                    {isParentApp && <ListIcon className={styles.listicon} onClick={() => toggleHandler()}/>}
                    <div className={styles.programName}>
                      {`${programData?.getCourse.name} - ${selectedContent?.name}`}
                    </div>
                    <div onClick={handleBack} className={styles.backBtn}>
                      <ChevronRight/>
                    </div>
                  </div>
                  {showList && (
                    <div className={styles.mobileLessonList}>
                      {units.map((unit, ind) => (
                        <SidebarUnit unit={unit as any} lastVisitedLesson={undefined} isParentApp={isParentApp} key={ind} />
                      ))}
                    </div>)
                  }
                </>
              )}
              <div className={`${styles.darkOverlayHidden} ${showList ? styles.mobileOverlay : ''}`} onClick={() => toggleHandler(true)}/>
              {selectedContent && <SelectedContent content={selectedContent} lesson={lesson} skill={currentUnit?.skill} />}
            </>
          )
        }
        {selectionLevel === 'Lesson' &&
        lesson &&
        !selectedContent &&
        <LessonHeader name={lesson.name} description={lesson.description || ''} image={lesson?.image || ''}/>
        }
      </div>
    </div>);

  return (
    <>
      {(!isMobile || isPortrait) && <Header/>}
      {isMobile && isPortrait ? (
        <div className={styles.rotate}>
          <RotateIcon width="60" height="60"/>
          <h3>Please rotate your device.</h3>
          Rotate your device to get better reading experience.
        </div>
      ) : normalTmpl}
    </>
  );
}

const ProgramDetails = () => {
  const { pathname } = useLocation();
  const isParentApp = pathname.startsWith('/parent');
  const isParentSpecialProgram = pathname.startsWith('/parent/specialProgram');

  return (
    <CourseContentProvider>
      <Component isParentApp={isParentApp} isParentSpecialProgram={isParentSpecialProgram} />
    </CourseContentProvider>
  );
}

export default ProgramDetails;
